import { HttpErrorResponse } from '@angular/common/http';
import { computed, effect, ResourceRef, signal } from '@angular/core';
import { rxResource, toObservable } from '@angular/core/rxjs-interop';
import {
  LoggedInUserDetailsEndpoint as ClinicLoggedInUserDetailsEndpoint,
  LoginEndpoint as ClinicLoginEndpoint,
  RegisterTempUserEndpoint,
} from '@clinic-endpoints';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  LoggedInUserDetailsEndpoint as PimsLoggedInUserDetailsEndpoint,
  LoginEndpoint as PimsLoginEndpoint,
} from '@pims-endpoints';
import { apiFinishedLoading, LocalStorageKeys, withEnvironment } from '@shared-utils';
import { Auth, Users } from '@types-lib';
import { EnvironmentAppNames } from 'environments';
import { catchError, combineLatest, filter, finalize, map, pipe, startWith, tap, throttleTime, throwError } from 'rxjs';
import { withAreaCodes } from './area-codes.feature';
import { LoggedInUserInitialState } from './logged-in-user.state';
import { withMfa } from './mfa.feature';

export const LoggedInUserStore = signalStore(
  { providedIn: 'root' },
  withState<Users.LoggedInUserState>(LoggedInUserInitialState),
  withEnvironment(),
  withProps(({ environment }) => ({
    _loginEndpoint:
      environment.constants.app_name === ('pims' as EnvironmentAppNames)
        ? new PimsLoginEndpoint()
        : new ClinicLoginEndpoint(),
    _loggedInUserDetailsEndpoint:
      environment.constants.app_name === ('pims' as EnvironmentAppNames)
        ? new PimsLoggedInUserDetailsEndpoint()
        : new ClinicLoggedInUserDetailsEndpoint(),
    _registerTempUserEndpoint: new RegisterTempUserEndpoint(),
    _loginTrigger: signal<Auth.AuthPostRequest | undefined>(undefined),
    _registerTempUserTrigger: signal<Auth.RegisterTempUserRequest | undefined>(undefined),
    _deleteTokenTrigger: signal<number | undefined>(undefined),
  })),
  withMfa(),
  withAreaCodes(),
  withMethods(store => {
    return {
      resetUserDetails() {
        patchState(store, { user: undefined });
      },
      setUserDetails(user: Users.User) {
        patchState(store, { user });
      },
      _frontendLogout: () => {
        patchState(store, { user: undefined });
        store.removeToken();
        store.removeAreaCodes();
      },
      logout: () => {
        // prevValue initializes to undefined so that it's not called right away.
        // From that point, if it's falsy, it will be forced a 0 and assume only 0 or 1 values
        store._deleteTokenTrigger.update(prevValue => ((prevValue || 0) + 1) % 2);
      },
      login: rxMethod<Auth.AuthPostRequest>(
        pipe(
          throttleTime(500),
          tap(loginInfo => store._loginTrigger.update(() => ({ ...loginInfo }))),
        ),
      ),
      registerTempUser: rxMethod<Auth.RegisterTempUserRequest>(
        pipe(
          throttleTime(500),
          tap(tempUserInfo => store._registerTempUserTrigger.update(() => ({ ...tempUserInfo }))),
        ),
      ),
    };
  }),
  withProps(store => {
    const _loginResource: ResourceRef<Auth.AuthPostResponse | undefined> = rxResource({
      params: () => {
        const loginRequest = store._loginTrigger();
        return loginRequest ? { ...loginRequest } : undefined;
      },
      stream: ({ params }) =>
        store._loginEndpoint.post(params as Auth.AuthPostRequest).pipe(
          tap(response => {
            // need to set the token in local storage manually here or the user details call triggered by the
            // store.setToken call won't be able to extract it from the localstorage yet
            if (response?.token) {
              window.localStorage.setItem(LocalStorageKeys.TOKEN, response.token);
              // remove isTempToken localstorage Flag after login
              if (store.isTempToken()) {
                store.setIsTempToken(false);
              }
            } else {
              window.localStorage.removeItem(LocalStorageKeys.TOKEN);
            }
            store.setMfaToken(response?.mfa_token || null);
            store.setToken(response?.token || null);
          }),
          catchError((err: HttpErrorResponse) => {
            console.log('Login Error:', err);
            patchState(store, { user: undefined });
            return throwError(() => Object.assign(new Error('Something went wrong'), err));
          }),
        ),
    });

    const _registerTempUserResource: ResourceRef<Auth.RegisterTempUserResponse | undefined> = rxResource({
      params: () => {
        const registerTempUserRequest = store._registerTempUserTrigger();
        return registerTempUserRequest ? { ...registerTempUserRequest } : undefined;
      },
      stream: ({ params }) =>
        store._registerTempUserEndpoint.put(params as Auth.RegisterTempUserRequest).pipe(
          tap(response => {
            // need to set the token in local storage manually here or the user details call triggered by the
            // store.setToken call won't be able to extract it from the localstorage yet
            if (response?.token) {
              window.localStorage.setItem(LocalStorageKeys.TOKEN, response.token);
              // remove isTempToken localstorage Flag after login
              if (store.isTempToken()) {
                store.setIsTempToken(false);
              }
            } else {
              window.localStorage.removeItem(LocalStorageKeys.TOKEN);
            }
            store.setMfaToken(response?.mfa_token || null);
            store.setToken(response?.token || null);
          }),
          catchError((err: HttpErrorResponse) => {
            console.log('Temp User Registration Error:', err);
            return throwError(() => Object.assign(new Error('Something went wrong'), err));
          }),
        ),
    });

    const _deleteTokenResource: ResourceRef<void> = rxResource({
      params: () => store._deleteTokenTrigger(),
      stream: () =>
        store._loginEndpoint.delete().pipe(
          catchError((err: HttpErrorResponse) => {
            console.error('Failed to remove token from Redis:', err);
            return throwError(() => Object.assign(new Error('Something went wrong'), err));
          }),
          finalize(() => store._frontendLogout()),
        ),
    });

    const _loadUserDetailsResource: ResourceRef<Users.User | undefined> = rxResource({
      params: () => {
        const token = store.token();
        return token ? { token } : undefined;
      },
      stream: () =>
        store._loggedInUserDetailsEndpoint.get().pipe(
          tap(user => {
            patchState(store, { user });
          }),
          catchError((err: HttpErrorResponse) => {
            console.error(err);
            store.resetUserDetails();
            return throwError(() => Object.assign(new Error('Something went wrong'), err));
          }),
        ),
    });

    const hasFinishedLoadingLoginApi$ = toObservable(_loginResource.status).pipe(apiFinishedLoading());
    const hasFinishedLoadingTempUserRegistrationApi$ = toObservable(_registerTempUserResource.status).pipe(
      apiFinishedLoading(),
    );
    const hasFinishedLoadingUserDetailsApi$ = toObservable(_loadUserDetailsResource.status).pipe(apiFinishedLoading());
    const hasFinishedLoadingAreaCodesApi$ = toObservable(store.areaCodesResource.status).pipe(apiFinishedLoading());

    const hasFinishedLoggingIn$ = combineLatest([
      hasFinishedLoadingLoginApi$.pipe(startWith('idle')),
      hasFinishedLoadingUserDetailsApi$.pipe(startWith('idle')),
    ]).pipe(
      map(([loginStatus, userDetailStatus]) => ({
        loginStatus,
        userDetailStatus,
        isSuccess: loginStatus === 'resolved' && userDetailStatus === 'resolved',
        hasError: loginStatus === 'error' || userDetailStatus === 'error',
      })),
      filter(status => status.hasError || status.isSuccess),
    );

    return {
      loginResource: _loginResource.asReadonly(),
      registerTempUserResource: _registerTempUserResource.asReadonly(),
      deleteTokenResource: _deleteTokenResource.asReadonly(),
      loadUserDetailsResource: _loadUserDetailsResource.asReadonly(),
      hasFinishedLoggingIn$: hasFinishedLoggingIn$,
      hasFinishedLoadingLoginApi$,
      hasFinishedLoadingTempUserRegistrationApi$,
      hasFinishedLoadingUserDetailsApi$,
      hasFinishedLoadingAreaCodesApi$,
    };
  }),
  withComputed(
    ({
      loginResource,
      areaCodesResource,
      loadUserDetailsResource,
      registerTempUserResource,
      changeMfaMethodResource,
      mfaOtpVerifyResource,
    }) => ({
      isLoading: computed<boolean>(
        () =>
          loginResource.isLoading() ||
          registerTempUserResource.isLoading() ||
          areaCodesResource.isLoading() ||
          loadUserDetailsResource.isLoading() ||
          changeMfaMethodResource.isLoading() ||
          mfaOtpVerifyResource.isLoading(),
      ),
    }),
  ),
  withHooks({
    onInit(store) {
      effect(() => {
        if (!store.token()) {
          store.resetUserDetails();
          store.removeAreaCodes();
        }
      });
    },
    onDestroy(store) {
      store.login.destroy();
    },
  }),
);
